import * as THREE from 'three'
import {
  GlbNameLookup,
  buildGlbNameMapping,
  matchesHintsWithMapping,
  nameMatchesArticle,
  type MeshHint,
} from '@/utils/glbMeshMatching'
import { groupOccurrences, type BaseWorld } from '@/utils/glbOccurrences'

type ResolvedInstances = {
  instances: THREE.Mesh[][]
  indexByMesh: Map<THREE.Mesh, number>
}

function hintKey(hint: MeshHint): string {
  const names = [...(hint.glbArticleNames ?? [hint.articleNumber])].sort()
  return `${names.join('\0')}${hint.requiredAncestors.join('\0')}`
}

export function useHintInstances(
  allMeshes: THREE.Mesh[],
  meshAncestorNames: Map<THREE.Mesh, string[]>,
  baseWorld: BaseWorld,
) {
  const resolvedByKey = new Map<string, ResolvedInstances>()
  let meshesByName: Map<string, THREE.Mesh[]> | null = null
  let nameLookup: GlbNameLookup | null = null

  function reset() {
    resolvedByKey.clear()
    meshesByName = null
    nameLookup = null
  }

  function nameIndex(): Map<string, THREE.Mesh[]> {
    if (meshesByName) return meshesByName
    const index = new Map<string, THREE.Mesh[]>()
    for (const mesh of allMeshes) {
      for (const name of new Set(meshAncestorNames.get(mesh) ?? [])) {
        const meshes = index.get(name)
        if (meshes) meshes.push(mesh)
        else index.set(name, [mesh])
      }
    }
    meshesByName = index
    return index
  }

  function resolve(hint: MeshHint): ResolvedInstances {
    const key = hintKey(hint)
    const cached = resolvedByKey.get(key)
    if (cached) return cached

    const index = nameIndex()
    nameLookup ??= new GlbNameLookup(index.keys())
    const articleSet = new Set(hint.glbArticleNames ?? [hint.articleNumber])
    const known = new Set([...articleSet].filter((article) => index.has(article)))
    const candidates = new Set<THREE.Mesh>()
    for (const name of nameLookup.candidateNames(articleSet)) {
      if (!nameMatchesArticle(name, articleSet, false, known)) continue
      for (const mesh of index.get(name)!) candidates.add(mesh)
    }
    const candidateMeshes = [...candidates]
    const mapping = buildGlbNameMapping(candidateMeshes, meshAncestorNames, [hint], known)
    const strict = candidateMeshes.filter((mesh) =>
      matchesHintsWithMapping(meshAncestorNames.get(mesh) ?? [], [hint], mapping, known),
    )
    const instances = groupOccurrences(
      strict.length > 0 ? strict : candidateMeshes,
      articleSet,
      known,
      baseWorld,
    )
    const indexByMesh = new Map<THREE.Mesh, number>()
    instances.forEach((meshes, i) => meshes.forEach((mesh) => indexByMesh.set(mesh, i)))

    const resolved = { instances, indexByMesh }
    resolvedByKey.set(key, resolved)
    return resolved
  }

  function instanceCount(hint: MeshHint): number {
    return resolve(hint).instances.length
  }

  function instanceOf(hint: MeshHint, mesh: THREE.Mesh): number | null {
    return resolve(hint).indexByMesh.get(mesh) ?? null
  }

  function instanceMeshes(hint: MeshHint, index: number): THREE.Mesh[] {
    return resolve(hint).instances[index] ?? []
  }

  function meshesOfInstances(hint: MeshHint, indexes: number[]): Set<THREE.Mesh> {
    const { instances } = resolve(hint)
    return new Set(indexes.flatMap((index) => instances[index] ?? []))
  }

  function allocatedOccurrenceFilter(
    hints: Array<MeshHint & { articleId: string }>,
  ): (articleId: string, meshes: THREE.Mesh[]) => boolean {
    const allowedByArticle = new Map<string, Set<THREE.Mesh> | null>()
    for (const hint of hints) {
      const allowed = allowedByArticle.get(hint.articleId)
      if (allowed === null) continue
      if (!hint.instanceIndexes) {
        allowedByArticle.set(hint.articleId, null)
        continue
      }
      const meshes = meshesOfInstances(hint, hint.instanceIndexes)
      allowedByArticle.set(hint.articleId, allowed ? new Set([...allowed, ...meshes]) : meshes)
    }
    return (articleId, meshes) => {
      const allowed = allowedByArticle.get(articleId)
      return !allowed || meshes.some((m) => allowed.has(m))
    }
  }

  return {
    reset,
    instanceCount,
    instanceOf,
    instanceMeshes,
    meshesOfInstances,
    allocatedOccurrenceFilter,
  }
}

export type HintInstances = ReturnType<typeof useHintInstances>
